import generateHash from '../utils/generateHash';
import { usePageMeta } from './usePageMeta';
import { useTranslation } from 'react-i18next';

type MessageMapper = (shareUrl: string) => string;
type EmailMapper = (
  shareUrl: string,
) => {
  subject: string;
  body: string;
};

interface MessageOverrides {
  whatsApp?: MessageMapper;
  twitter?: MessageMapper;
  sms?: MessageMapper;
  email?: EmailMapper;
}

type shareUrls = {
  [key: string]: any;
};

export function useShareUrls(
  messageMappers: MessageOverrides,
  shareUrl?: string,
): shareUrls {
  const facebook = useFacebook(shareUrl);
  const facebookMessenger = useFacebookMessenger(shareUrl);
  const twitter = useTwitter(shareUrl, messageMappers.twitter);
  const email = useEmail(shareUrl, messageMappers.email);
  const whatsApp = useWhatsapp(shareUrl, messageMappers.whatsApp);
  const sms = useSms(shareUrl, messageMappers.sms);
  const linkedin = useLinkedIn(shareUrl);
  const copyLink = useCopyLink(shareUrl);

  const instagram = useInstagram(shareUrl);
  const instagramShareModal = useInstagramShareModal(shareUrl);
  const tiktok = useTikTok(shareUrl);
  const youtube = useYoutube(shareUrl);
  const slack = useSlack(shareUrl);
  const teams = useTeams(shareUrl);
  const linktree = useLinktree(shareUrl);
  const snapchat = useSnapchat(shareUrl);
  const snapchatShareModal = useSnapchatShareModal(shareUrl);
  const reddit = useReddit(shareUrl);
  const twitch = useTwitch(shareUrl);

  return {
    facebook,
    facebookMessenger,
    twitter,
    email,
    whatsApp,
    sms,
    linkedin,
    copyLink,
    instagram,
    instagramShareModal,
    tiktok,
    youtube,
    slack,
    teams,
    linktree,
    snapchat,
    snapchatShareModal,
    reddit,
    twitch,
  };
}

function useShareUrl(shareUrl?: string, queryString?: string) {
  const { currentUrl } = usePageMeta();

  if (shareUrl) return shareUrl;

  if (!queryString) return currentUrl;

  return currentUrl.includes('?')
    ? `${currentUrl}&${queryString}`
    : `${currentUrl}?${queryString}`;
}

function useFacebook(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=facebook&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-share&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useFacebookMessenger(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=messenger&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-share&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useCopyLink(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=copyLink&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-share&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useTwitter(shareUrl?: string, messageMapper?: MessageMapper) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=Twitter&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-tweet&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);
  const message = messageMapper
    ? messageMapper(url)
    : t('common.shareMessageFallback');

  return {
    url,
    message,
  };
}

function useEmail(
  shareUrl?: string,
  messageMapper?: (url: string) => { subject: string; body: string },
) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=Sharethis&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-email&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  const { subject, body } = messageMapper
    ? messageMapper(url)
    : { subject: '', body: '' };

  return {
    subject,
    body,
  };
}

function useWhatsapp(shareUrl?: string, messageMapper?: MessageMapper) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=whatsapp&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-whatsapp&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  const message = messageMapper
    ? messageMapper(url)
    : t('common.shareMessageFallback');

  return {
    url,
    message,
  };
}

function useSms(shareUrl?: string, messageMapper?: MessageMapper) {
  const { t } = useTranslation('common', { useSuspense: false });
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=sms&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-sms&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  const message = messageMapper
    ? messageMapper(url)
    : t('common.shareMessageFallback');

  return {
    url,
    message,
  };
}

function useLinkedIn(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=LinkedIn&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-linkedin&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useInstagram(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=instagram&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-instagram&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useInstagramShareModal(shareUrl?: string) {
  const url = useShareUrl(shareUrl);

  const queryString = `utm_source=IG`;
  const utmUrl = useShareUrl(shareUrl, queryString);

  return {
    url,
    utmUrl,
  };
}

function useTikTok(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=tiktok&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-tiktok&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useYoutube(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=youtube&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-youtube&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useSlack(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=slack&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-slack&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useTeams(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=teams&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-teams&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useLinktree(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=linktree&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-linktree&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useSnapchat(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=snapchat&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-snapchat&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useSnapchatShareModal(shareUrl?: string) {
  const url = useShareUrl(shareUrl);

  const queryString = `utm_source=SN`;
  const utmUrl = useShareUrl(shareUrl, queryString);

  return {
    url,
    utmUrl,
  };
}

function useReddit(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=reddit&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-reddit&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}

function useTwitch(shareUrl?: string) {
  const { pageType, pageSlug } = usePageMeta();

  const queryString = `utm_source=twitch&utm_medium=${pageType}&utm_content=${pageSlug}&utm_campaign=pfp-twitch&utm_term=${generateHash()}`;
  const url = useShareUrl(shareUrl, queryString);

  return {
    url,
  };
}
